import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {UnitMeasurementUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';
import {combineLatest} from 'rxjs'

import {
    UnitMeasurement,
    UnitMeasurementOnServerCreated,
    selectUnitMeasurementsActionLoading,
    selectUnitMeasurementById,
    selectLastCreatedUnitMeasurementId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-unit-measurement-edit',
    templateUrl: './unit-measurement-edit.component.html',
    styleUrls: ['./unit-measurement-edit.component.scss'],
})


export class UnitMeasurementEditComponent implements OnInit, OnDestroy {

    model: UnitMeasurement;
    oldModel: UnitMeasurement;

    unitMeasurementForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    readonly = false

    /**
     * Component constructor
     *
     * @param subheaderService: SubheaderService
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param store: Store<AppState>
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectUnitMeasurementsActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectUnitMeasurementById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initUnitMeasurement();
                    }
                });
            } else {
                this.model = new UnitMeasurement();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initUnitMeasurement();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Interaction types', page: `../default/admin-management/linked-tables/unit-measurements`},
            {
                title: this.getComponentTitle(), page: id > 0 ? `../default/admin-management/linked-tables/unit-measurements/edit/${id}`
                    : `../default/admin-management/linked-tables/unit-measurements/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initUnitMeasurement() {
        this.createForm();
    }


    createForm() {
        this.unitMeasurementForm = this.fb.group({
            name: [this.model.name, Validators.required],
            length: [this.model.length, Validators.required],
            area: [this.model.area, Validators.required],
            weight: [this.model.weight, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('SYSTEM_MEASUREMENT.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('SYSTEM_MEASUREMENT.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.unitMeasurementForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.unitMeasurementForm.markAsPristine();
        this.unitMeasurementForm.markAsUntouched();
        this.unitMeasurementForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.unitMeasurementForm.controls;
        /** check form */
        if (this.unitMeasurementForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateUnitMeasurement(this.model);
        } else {
            this.createUnitMeasurement(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: UnitMeasurementModel
     */
    updateUnitMeasurement(_model: UnitMeasurement) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateUnitMeasurement: Update<UnitMeasurement> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new UnitMeasurementUpdated({
            partialunitMeasurement: updateUnitMeasurement,
            unitMeasurement: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: UnitMeasurementModel
     */
    createUnitMeasurement(_model: UnitMeasurement) {
        this.store.dispatch(new UnitMeasurementOnServerCreated({unitMeasurement: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedUnitMeasurementId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0 &&
            this.model.length && this.model.length.length > 0 &&
            this.model.area && this.model.area.length > 0 &&
            this.model.weight && this.model.weight.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }


    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name
                || this.model.length != this.oldModel.length
                || this.model.area != this.oldModel.area
                || this.model.weight != this.oldModel.weight;
        }
        return false;
    }
}
